import { HonorPeriod, interactionBreakdown } from './honor-period';

export type HonorKind = 'followers' | 'donator' | 'receives' | 'interactions';

export interface HonorRow {
  userId: string;
  fullName: string;
  userName: string | null;
  avatar: string | null;
  value: number;
  detail?: string;
}

const asTrimmedOrNull = (v: unknown): string | null => {
  if (typeof v !== 'string') return null;
  const trimmed = v.trim();
  return trimmed.length > 0 ? trimmed : null;
};

/**
 * Một số tài khoản dùng chính địa chỉ email làm tên hoặc username. Trên các bảng công khai chỉ hiện
 * phần trước dấu @ để không lộ email ("huyloc7@gmail.com" thành "huyloc7"). Chuỗi khác giữ nguyên.
 */
export const maskEmailLike = (value: string): string => {
  const v = value.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? v.slice(0, v.indexOf('@')) : value;
};

/**
 * Tài khoản đã xoá không còn fullName lẫn userName (sau trim) - dùng chung cho mọi bảng đọc
 * thẳng từ API (khung "Bảng xếp hạng" trang chủ) để loại các dòng này trước khi cắt top 5,
 * cùng quy tắc `toHonorRows` áp dụng cho trang /vinh-danh.
 */
export const hasHonorDisplayName = (item: { fullName?: unknown; userName?: unknown } | null | undefined): boolean => {
  if (!item) return false;
  return Boolean(asTrimmedOrNull(item.fullName) || asTrimmedOrNull(item.userName));
};

/**
 * Chuẩn hoá phản hồi 4 nguồn (followers/donator/receives/interactions) về
 * HonorRow[] dùng chung cho bảng vinh danh. Bỏ dòng không có cả fullName lẫn
 * userName (sau trim) - đây là dấu hiệu tài khoản đã bị xoá. fullName rỗng
 * (hoặc toàn khoảng trắng) thì dùng userName thay thế.
 */
export const toHonorRows = (kind: HonorKind, response: any): HonorRow[] => {
  let items: any[];
  if (kind === 'followers') {
    items = Array.isArray(response?.data) ? response.data : [];
  } else if (kind === 'donator' || kind === 'receives') {
    items = Array.isArray(response?.items) ? response.items : [];
  } else {
    items = Array.isArray(response) ? response : [];
  }

  const rows: HonorRow[] = [];

  for (const item of items) {
    if (!item) continue;
    if (!item.userId) continue; // thiếu userId - dữ liệu hỏng, bỏ qua

    const fullName = asTrimmedOrNull(item.fullName);
    const userName = asTrimmedOrNull(item.userName);
    if (!fullName && !userName) continue; // tài khoản đã xoá

    let value: number;
    let detail: string | undefined;

    if (kind === 'followers') {
      value = Number(item.totalFollow ?? 0);
    } else if (kind === 'donator' || kind === 'receives') {
      value = Number(item.totalAmount ?? 0);
    } else {
      value = Number(item.score ?? 0);
      detail = interactionBreakdown({
        likes: item.likes,
        comments: item.comments,
        shares: item.shares,
      });
    }

    rows.push({
      userId: item.userId,
      fullName: maskEmailLike(fullName || (userName as string)),
      userName,
      avatar: item.avatar ?? null,
      value,
      ...(detail !== undefined ? { detail } : {}),
    });
  }

  return rows;
};

const UNIT_BY_KIND: Record<HonorKind, string> = {
  followers: 'người theo dõi',
  donator: 'Ca',
  receives: 'Ca',
  interactions: 'điểm',
};

/** "44 người theo dõi" | "1.450.000 Ca" | "250 điểm". */
export const formatHonorValue = (kind: HonorKind, value: number): string => {
  return `${value.toLocaleString('vi-VN')} ${UNIT_BY_KIND[kind]}`;
};

/** Khoá tab trên URL `/vinh-danh?tab=&ky=`. */
export type HonorTabKey = 'tuong-tac' | 'donate' | 'nhan-donate' | 'theo-doi';
/** Khoá kỳ trên URL `/vinh-danh?tab=&ky=`. */
export type HonorPeriodKey = 'tuan' | 'thang' | 'tat-ca';

export interface HonorQuery {
  kind: HonorKind;
  period: HonorPeriod;
  tabKey: HonorTabKey;
  periodKey: HonorPeriodKey;
}

const TAB_KEY_TO_KIND: Record<HonorTabKey, HonorKind> = {
  'tuong-tac': 'interactions',
  donate: 'donator',
  'nhan-donate': 'receives',
  'theo-doi': 'followers',
};

const PERIOD_KEY_TO_PERIOD: Record<HonorPeriodKey, HonorPeriod> = {
  tuan: 'week',
  thang: 'month',
  'tat-ca': 'all',
};

const DEFAULT_TAB_KEY: HonorTabKey = 'tuong-tac';
const DEFAULT_PERIOD_KEY: HonorPeriodKey = 'tuan';

/**
 * Đọc `?tab=&ky=` trên URL trang `/vinh-danh` thành lựa chọn bảng + kỳ dùng
 * để gọi API. Giá trị lạ (thiếu, sai chính tả, khoá không tồn tại) mặc định
 * về tab "Được tương tác nhiều nhất" (tuong-tac) + kỳ "Tuần này" (tuan).
 */
export const parseHonorQuery = (search: URLSearchParams): HonorQuery => {
  const tabRaw = search.get('tab');
  const periodRaw = search.get('ky');

  const tabKey: HonorTabKey =
    tabRaw && tabRaw in TAB_KEY_TO_KIND ? (tabRaw as HonorTabKey) : DEFAULT_TAB_KEY;
  const periodKey: HonorPeriodKey =
    periodRaw && periodRaw in PERIOD_KEY_TO_PERIOD
      ? (periodRaw as HonorPeriodKey)
      : DEFAULT_PERIOD_KEY;

  return {
    kind: TAB_KEY_TO_KIND[tabKey],
    period: PERIOD_KEY_TO_PERIOD[periodKey],
    tabKey,
    periodKey,
  };
};
